import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Layout } from '../components/Layout';
import { asinDatabaseAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Search, Upload, Download } from 'lucide-react';

const PAGE_SIZE = 100;

export const ASINDatabase = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [uploading, setUploading] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const fileInputRef = useRef(null);
  const labelInputRef = useRef(null);

  const [selectedEntries, setSelectedEntries] = useState(new Set());
  const [isDragActive, setIsDragActive] = useState(false);
  const [labelTarget, setLabelTarget] = useState(null);
  const [sortBy, setSortBy] = useState('ean');
  const [sortDir, setSortDir] = useState('asc');

  const [formData, setFormData] = useState({
    ean: '',
    asin: '',
    sku: '',
    name: '',
    bagging: false,
    dangerous: false,
    amz_code: '',
    weight_grams: '',
    estimated_shipping_price_usd: '',
  });

  const fetchEntries = useCallback(async (pageToLoad = 1, search = '', sortField = sortBy, sortDirection = sortDir) => {
    setLoading(true);
    try {
      const response = await asinDatabaseAPI.getAll({
        page: pageToLoad,
        limit: PAGE_SIZE,
        search: search || undefined,
        sort_by: sortField,
        sort_dir: sortDirection,
      });
      const { items, page: serverPage, total_pages, total_items } = response.data;
      setEntries(items);
      setTotalPages(total_pages || 1);
      setTotalItems(total_items || 0);
      setSelectedEntries(new Set());

      if (serverPage && serverPage !== pageToLoad) {
        setPage(serverPage);
      }
    } catch (error) {
      toast.error('Failed to load ASIN database');
    } finally {
      setLoading(false);
    }
  }, [sortBy, sortDir]);

  useEffect(() => {
    fetchEntries(1);
  }, [fetchEntries]);

  useEffect(() => {
    if (!uploading) {
      setImportProgress(0);
      return;
    }
    const interval = setInterval(() => {
      setImportProgress((prev) => (prev >= 100 ? 0 : prev + 10));
    }, 400);
    return () => clearInterval(interval);
  }, [uploading]);

  const handleSubmit = async () => {
    try {
      const data = {
        ean: formData.ean,
        asin: formData.asin,
        sku: formData.sku || undefined,
        name: formData.name || undefined,
        bagging: !!formData.bagging,
        dangerous: !!formData.dangerous,
        amz_code: formData.amz_code || undefined,
        weight_grams: parseFloat(formData.weight_grams),
        estimated_shipping_price_usd: parseFloat(formData.estimated_shipping_price_usd),
      };

      if (editingEntry) {
        await asinDatabaseAPI.update(editingEntry.id, data);
        toast.success('Entry updated');
      } else {
        await asinDatabaseAPI.create(data);
        toast.success('Entry created');
      }

      setDialogOpen(false);
      resetForm();
      fetchEntries(page, searchQuery, sortBy, sortDir);
    } catch (error) {
      toast.error('Failed to save entry');
    }
  };

  const handleEdit = (entry) => {
    setEditingEntry(entry);
    setFormData({
      ean: entry.ean,
      asin: entry.asin,
      sku: entry.sku || '',
      name: entry.name || '',
      bagging: !!entry.bagging,
      dangerous: !!entry.dangerous,
      amz_code: entry.amz_code || '',
      weight_grams: entry.weight_grams?.toString() || '',
      estimated_shipping_price_usd: entry.estimated_shipping_price_usd?.toString() || '',
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this entry?')) return;
    try {
      await asinDatabaseAPI.delete(id);
      toast.success('Entry deleted');
      fetchEntries(page, searchQuery, sortBy, sortDir);
    } catch (error) {
      toast.error('Failed to delete entry');
    }
  };

  const resetForm = () => {
    setEditingEntry(null);
    setFormData({
      ean: '',
      asin: '',
      sku: '',
      name: '',
      bagging: false,
      dangerous: false,
      amz_code: '',
      weight_grams: '',
      estimated_shipping_price_usd: '',
    });
  };

  const toggleSelectEntry = (id) => {
    setSelectedEntries((prev) => {
      const updated = new Set(prev);
      if (updated.has(id)) {
        updated.delete(id);
      } else {
        updated.add(id);
      }
      return updated;
    });
  };

  const allEntriesSelected = entries.length > 0 && selectedEntries.size === entries.length;

  const toggleSelectAll = () => {
    if (allEntriesSelected) {
      setSelectedEntries(new Set());
      return;
    }
    setSelectedEntries(new Set(entries.map((entry) => entry.id)));
  };

  const handleBulkDelete = async () => {
    if (selectedEntries.size === 0) {
      toast.info('Select at least one entry');
      return;
    }
    if (!window.confirm(`Delete ${selectedEntries.size} entries?`)) return;
    try {
      await Promise.all(
        Array.from(selectedEntries).map((id) => asinDatabaseAPI.delete(id))
      );
      toast.success(`Deleted ${selectedEntries.size} entries`);
      setSelectedEntries(new Set());
      fetchEntries(page, searchQuery, sortBy, sortDir);
    } catch (error) {
      toast.error('Failed to delete entries');
    }
  };

  const handleDeleteAll = async () => {
    if (totalItems === 0) {
      toast.info('No entries to delete');
      return;
    }
    const scopeLabel = searchQuery ? `all entries matching "${searchQuery}"` : 'all entries';
    if (!window.confirm(`Are you sure you want to delete ${scopeLabel}? This action cannot be undone.`)) {
      return;
    }
    setDeletingAll(true);
    try {
      const params = searchQuery ? { search: searchQuery } : {};
      const response = await asinDatabaseAPI.deleteAll(params);
      const deletedCount = response.data?.deleted ?? 0;
      toast.success(`Deleted ${deletedCount} entries`);
      setPage(1);
      fetchEntries(1, searchQuery, sortBy, sortDir);
    } catch (error) {
      toast.error('Failed to delete entries');
    } finally {
      setDeletingAll(false);
    }
  };

  const handlePageChange = (nextPage) => {
    if (nextPage < 1 || nextPage > totalPages) return;
    setPage(nextPage);
    fetchEntries(nextPage, searchQuery, sortBy, sortDir);
  };

  const handleFileImport = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const response = await asinDatabaseAPI.importCsv(file);
      const { inserted = 0, updated = 0, skipped = 0 } = response.data;
      toast.success(`Import completed: +${inserted}, updated ${updated}, skipped ${skipped}`);
      fetchEntries(page, searchQuery, sortBy, sortDir);
    } catch (error) {
      toast.error('Failed to import CSV');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragActive(false);
    const file = event.dataTransfer.files?.[0];
    handleFileImport(file);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    if (!isDragActive) setIsDragActive(true);
  };

  const handleDragLeave = (event) => {
    if (event.relatedTarget && event.currentTarget.contains(event.relatedTarget)) return;
    setIsDragActive(false);
  };

  const handleLabelFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !labelTarget) return;
    try {
      await asinDatabaseAPI.uploadLabel(labelTarget, file);
      toast.success('Label uploaded');
      fetchEntries(page, searchQuery, sortBy, sortDir);
    } catch (error) {
      toast.error('Failed to upload label');
    } finally {
      setLabelTarget(null);
      if (labelInputRef.current) {
        labelInputRef.current.value = '';
      }
    }
  };

  const handleDownloadLabel = async (entry) => {
    try {
      const response = await asinDatabaseAPI.downloadLabel(entry.id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${entry.ean || 'label'}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download label');
    }
  };

  const hasEntries = entries.length > 0 && totalItems > 0;
  const startItem = hasEntries ? (page - 1) * PAGE_SIZE + 1 : 0;
  const endItem = hasEntries ? startItem + entries.length - 1 : 0;
  const displayTotalPages = totalPages > 0 ? totalPages : 1;
  const getSortIndicator = (field) => {
    if (sortBy !== field) return '';
    return sortDir === 'asc' ? '▲' : '▼';
  };
  const handleSort = (field) => {
    const nextDir = sortBy === field ? (sortDir === 'asc' ? 'desc' : 'asc') : 'asc';
    setSortBy(field);
    setSortDir(nextDir);
    setPage(1);
    fetchEntries(1, searchQuery, field, nextDir);
  };

  return (
    <Layout>
      <div className="page-header">
        <h1 className="page-title" data-testid="asin-database-title">ASIN & Shipping Price Database</h1>
        <p className="page-subtitle">Backup table for product weights and estimated shipping prices</p>
      </div>

      <Card data-testid="asin-database-card">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg">Database Entries</CardTitle>
          <div className="flex gap-2 flex-wrap items-center">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Search EAN or ASIN..."
                className="pl-9 w-64"
                value={searchValue}
                onChange={(e) => {
                  const value = e.target.value;
                  setSearchValue(value);
                  setSearchQuery(value);
                  setPage(1);
                  fetchEntries(1, value, sortBy, sortDir);
                }}
                data-testid="asin-search-input"
              />
            </div>
            <input
              type="file"
              accept=".csv,.CSV,text/csv"
              ref={fileInputRef}
              onChange={(event) => handleFileImport(event.target.files?.[0])}
              className="hidden"
            />
            <input
              type="file"
              accept="application/pdf"
              ref={labelInputRef}
              onChange={handleLabelFileChange}
              className="hidden"
            />
            <div
              className={`border rounded-md p-4 text-sm text-slate-600 cursor-pointer transition ${
                isDragActive ? 'border-slate-900 bg-slate-50' : 'border-dashed border-slate-300'
              }`}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <p className="font-medium">Import CSV</p>
              <p>Drag & drop your file here or click to browse.</p>
              <p className="mt-2 text-xs text-slate-500">
                Expected headers: EAN, ASIN, Weight, Est. Shipping, SKU, Name, SACHET, DG, AMZ CODE.
              </p>
            </div>
            <Button
              variant="destructive"
              onClick={handleBulkDelete}
              disabled={selectedEntries.size === 0}
              data-testid="bulk-delete-asin-btn"
            >
              Delete Selected ({selectedEntries.size})
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteAll}
              disabled={deletingAll || totalItems === 0}
              data-testid="delete-all-asin-btn"
            >
              {deletingAll ? 'Deleting…' : 'Delete All'}
            </Button>
            {uploading && (
              <div className="flex flex-col gap-1 items-start text-sm text-slate-500">
                <p>Processing file, this may take a few minutes...</p>
                <div className="w-48 h-2 bg-slate-200 rounded">
                  <div
                    className="h-2 bg-slate-600 rounded transition-all duration-300"
                    style={{ width: `${importProgress}%` }}
                  />
                </div>
              </div>
            )}
            <Dialog open={dialogOpen} onOpenChange={(open) => {
              setDialogOpen(open);
              if (!open) resetForm();
            }}>
              <DialogTrigger asChild>
                <Button data-testid="add-asin-entry-btn">
                  <Plus size={16} className="mr-2" />
                  Add Entry
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{editingEntry ? 'Edit Entry' : 'Add New Entry'}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div>
                    <Label>EAN</Label>
                    <Input
                      value={formData.ean}
                      onChange={(e) => setFormData({ ...formData, ean: e.target.value })}
                      placeholder="4006381333931"
                      data-testid="asin-ean-input"
                    />
                  </div>
                  <div>
                    <Label>ASIN</Label>
                    <Input
                      value={formData.asin}
                      onChange={(e) => setFormData({ ...formData, asin: e.target.value })}
                      placeholder="B09XYZ1234"
                      data-testid="asin-asin-input"
                    />
                  </div>
                  <div>
                    <Label>Name</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Product name"
                      data-testid="asin-name-input"
                    />
                  </div>
                  <div>
                    <Label>SKU (optional)</Label>
                    <Input
                      value={formData.sku}
                      onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                      placeholder="SKU-12345"
                      data-testid="asin-sku-input"
                    />
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="bagging"
                        checked={formData.bagging}
                        onCheckedChange={(checked) => setFormData({ ...formData, bagging: checked })}
                      />
                      <Label htmlFor="bagging">Bagging required</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="dangerous"
                        checked={formData.dangerous}
                        onCheckedChange={(checked) => setFormData({ ...formData, dangerous: checked })}
                      />
                      <Label htmlFor="dangerous">Dangerous</Label>
                    </div>
                  </div>
                  <div>
                    <Label>AMZ Code</Label>
                    <Input
                      value={formData.amz_code}
                      onChange={(e) => setFormData({ ...formData, amz_code: e.target.value })}
                      placeholder="FNSKU..."
                      data-testid="asin-amz-input"
                    />
                  </div>
                  <div>
                    <Label>Weight (grams)</Label>
                    <Input
                      type="number"
                      value={formData.weight_grams}
                      onChange={(e) => setFormData({ ...formData, weight_grams: e.target.value })}
                      placeholder="350"
                      data-testid="asin-weight-input"
                    />
                  </div>
                  <div>
                    <Label>Estimated Shipping Price (USD)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.estimated_shipping_price_usd}
                      onChange={(e) => setFormData({ ...formData, estimated_shipping_price_usd: e.target.value })}
                      placeholder="1.50"
                      data-testid="asin-shipping-input"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                  <Button onClick={handleSubmit} data-testid="save-asin-entry-btn">
                    {editingEntry ? 'Update' : 'Create'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <Input
                      type="checkbox"
                      className="accent-slate-900 h-4 w-4"
                      checked={allEntriesSelected}
                      onChange={toggleSelectAll}
                    />
                  </TableHead>
                  <TableHead>
                    <button type="button" className="flex items-center gap-1" onClick={() => handleSort('ean')}>
                      EAN <span>{getSortIndicator('ean')}</span>
                    </button>
                  </TableHead>
                  <TableHead>
                    <button type="button" className="flex items-center gap-1" onClick={() => handleSort('asin')}>
                      ASIN <span>{getSortIndicator('asin')}</span>
                    </button>
                  </TableHead>
                  <TableHead>
                    <button type="button" className="flex items-center gap-1" onClick={() => handleSort('sku')}>
                      SKU <span>{getSortIndicator('sku')}</span>
                    </button>
                  </TableHead>
                  <TableHead>
                    <button type="button" className="flex items-center gap-1" onClick={() => handleSort('name')}>
                      Name <span>{getSortIndicator('name')}</span>
                    </button>
                  </TableHead>
                  <TableHead>
                    <button type="button" className="flex items-center gap-1" onClick={() => handleSort('bagging')}>
                      Bagging <span>{getSortIndicator('bagging')}</span>
                    </button>
                  </TableHead>
                  <TableHead>
                    <button type="button" className="flex items-center gap-1" onClick={() => handleSort('dangerous')}>
                      Dangerous <span>{getSortIndicator('dangerous')}</span>
                    </button>
                  </TableHead>
                  <TableHead>
                    <button type="button" className="flex items-center gap-1" onClick={() => handleSort('amz_code')}>
                      AMZ Code <span>{getSortIndicator('amz_code')}</span>
                    </button>
                  </TableHead>
                  <TableHead className="text-right">
                    <button type="button" className="flex w-full items-center justify-end gap-1" onClick={() => handleSort('weight')}>
                      Weight (g) <span>{getSortIndicator('weight')}</span>
                    </button>
                  </TableHead>
                  <TableHead className="text-right">
                    <button type="button" className="flex w-full items-center justify-end gap-1" onClick={() => handleSort('shipping')}>
                      Est. Shipping ($) <span>{getSortIndicator('shipping')}</span>
                    </button>
                  </TableHead>
                  <TableHead>Label</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={12} className="text-center py-8 text-slate-500">
                      Loading entries...
                    </TableCell>
                  </TableRow>
                ) : (
                  <>
                    {entries.map((entry) => (
                      <TableRow key={entry.id} data-testid={`asin-row-${entry.id}`}>
                        <TableCell>
                          <Input
                            type="checkbox"
                            className="accent-slate-900 h-4 w-4"
                            checked={selectedEntries.has(entry.id)}
                            onChange={() => toggleSelectEntry(entry.id)}
                            aria-label="Select entry"
                          />
                        </TableCell>
                        <TableCell className="font-mono text-sm">{entry.ean}</TableCell>
                        <TableCell className="font-mono text-sm">{entry.asin}</TableCell>
                        <TableCell className="font-mono text-sm">{entry.sku || '—'}</TableCell>
                        <TableCell>{entry.name || '—'}</TableCell>
                        <TableCell>
                          <span className={`badge ${entry.bagging ? 'badge-warning' : 'badge-neutral'}`}>
                            {entry.bagging ? 'Yes' : 'No'}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className={`badge ${entry.dangerous ? 'badge-danger' : 'badge-success'}`}>
                            {entry.dangerous ? 'Yes' : 'No'}
                          </span>
                        </TableCell>
                        <TableCell>{entry.amz_code || '—'}</TableCell>
                        <TableCell className="text-right">{entry.weight_grams ? entry.weight_grams : '—'}</TableCell>
                        <TableCell className="text-right">
                          ${Number(entry.estimated_shipping_price_usd ?? 0).toFixed(2)}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setLabelTarget(entry.id);
                                labelInputRef.current?.click();
                              }}
                            >
                              <Upload size={14} /> Upload
                            </Button>
                            {entry.label_pdf_url ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDownloadLabel(entry)}
                              >
                                <Download size={14} /> Download
                              </Button>
                            ) : (
                              <span className="text-xs text-slate-500">No file</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleEdit(entry)}
                              data-testid={`edit-asin-${entry.id}`}
                            >
                              <Pencil size={16} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDelete(entry.id)}
                              data-testid={`delete-asin-${entry.id}`}
                            >
                              <Trash2 size={16} className="text-red-500" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                    {entries.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={12} className="text-center py-8 text-slate-500">
                          {searchQuery ? 'No entries match your search' : 'No entries in database. Add your first entry.'}
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                )}
              </TableBody>
            </Table>
          </div>
          <div className="flex flex-col gap-3 border-t border-slate-100 px-6 py-4 text-sm text-slate-600 sm:flex-row sm:items-center sm:justify-between">
            <p>
              Showing {startItem}-{endItem} of {totalItems} entries (Page {Math.max(page, 1)} of {displayTotalPages})
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => handlePageChange(page - 1)}
                disabled={page <= 1}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                onClick={() => handlePageChange(page + 1)}
                disabled={page >= totalPages || totalPages === 0}
              >
                Next
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </Layout>
  );
};
